<template>
  <b-modal
    :visible="value"
    @hidden="$emit('input', false)"
    title="Ringkasan Data Finansial Proyek"
    title-class="font-weight-bold text-dark"
    size="lg"
    centered
    hide-footer
    no-close-on-backdrop
  >
    <div v-if="loading" class="text-center my-5">
      <b-spinner variant="primary" label="Memuat data..."></b-spinner>
      <p class="mt-2 text-muted">Sedang memuat data finansial...</p>
    </div>

    <div v-else>
      <div class="mb-4 p-3 bg-light rounded border row mx-0">
        <div class="col-sm-6">
          <small class="text-muted d-block font-weight-bold"
            >KODE PROJECT</small
          >
          <span
            class="font-weight-bold text-dark text-monospace font-size-15"
            >{{ projectCode || '-' }}</span
          >
        </div>
        <div class="col-sm-6 text-sm-right mt-2 mt-sm-0">
          <small class="text-muted d-block font-weight-bold"
            >NAMA PROJECT</small
          >
          <span class="font-weight-bold text-primary font-size-15">{{
            projectName || '-'
          }}</span>
        </div>
      </div>

      <div class="card border-0 shadow-none">
        <div class="card-body p-0">
          <table class="table table-sm table-borderless mb-0 custom-modal-info">
            <tbody>
              <tr>
                <td
                  class="font-weight-bold text-dark text-left align-middle"
                  width="55%"
                >
                  <div class="d-flex">
                    <span class="list-number">1.</span>
                    <span>Nilai Proyek</span>
                  </div>
                </td>
                <td
                  class="text-right align-middle font-weight-bold text-success font-size-15"
                  width="30%"
                >
                  {{ formatRupiah(nilaiProyek) }}
                </td>
                <td class="text-right align-middle" width="15%"></td>
              </tr>

              <tr class="border-top-dash">
                <td class="font-weight-bold text-dark text-left align-middle">
                  <div class="d-flex">
                    <span class="list-number">2.</span>
                    <span>Beban Proyek</span>
                  </div>
                </td>
                <td class="text-right align-middle font-weight-bold text-dark">
                  {{ formatRupiah(bebanProyek) }}
                </td>
                <td class="text-right align-middle"></td>
              </tr>

              <tr class="border-top-dash">
                <td class="font-weight-bold text-dark text-left align-middle">
                  <div class="d-flex">
                    <span class="list-number">3.</span>
                    <span>Margin</span>
                  </div>
                </td>

                <td
                  class="text-right align-middle font-weight-bold font-size-15"
                  :class="{
                    'text-success': nilaiProyek > bebanProyek,
                    'text-danger': nilaiProyek < bebanProyek,
                    'text-dark': nilaiProyek === bebanProyek,
                  }"
                >
                  {{
                    (((nilaiProyek - bebanProyek) / bebanProyek) * 100).toFixed(
                      2
                    )
                  }}%
                </td>

                <td class="text-right align-middle"></td>
              </tr>

              <tr class="border-top-dash row-header-group">
                <td
                  colspan="3"
                  class="font-weight-bold text-dark text-left align-middle"
                >
                  <div class="d-flex">
                    <span class="list-number">4.</span>
                    <span>Penagihan Project</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td class="text-dark text-left align-middle sub-label-row">
                  a. Ditagih ke Pemberi Kerja
                </td>
                <td
                  class="text-right align-middle font-weight-medium text-info"
                >
                  {{ formatRupiah(sudahDitagih) }}
                </td>
                <td class="text-right align-middle">
                  <span
                    class="badge badge-custom bg-success-light text-success-dark"
                  >
                    {{ hitungPersentase(sudahDitagih, nilaiProyek) }}
                  </span>
                </td>
              </tr>

              <tr>
                <td class="text-dark text-left align-middle sub-label-row">
                  b. Belum ditagih ke Pemberi Kerja
                </td>
                <td
                  class="text-right align-middle font-weight-medium text-warning"
                >
                  {{ formatRupiah(belumDitagih) }}
                </td>
                <td class="text-right align-middle">
                  <span
                    class="badge badge-custom"
                    :class="
                      belumDitagih > 0
                        ? 'bg-danger-light text-danger-dark'
                        : 'bg-secondary-light text-secondary-dark'
                    "
                  >
                    {{ hitungPersentase(belumDitagih, nilaiProyek) }}
                  </span>
                </td>
              </tr>

              <tr class="border-top-dash row-header-group">
                <td
                  colspan="3"
                  class="font-weight-bold text-dark text-left align-middle"
                >
                  <div class="d-flex">
                    <span class="list-number">5.</span>
                    <span>Pembayaran Project</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td class="text-dark text-left align-middle sub-label-row">
                  a. Sudah dibayar Pemberi Kerja
                </td>
                <td
                  class="text-right align-middle font-weight-medium text-primary"
                >
                  {{ formatRupiah(sudahBayarCustomer) }}
                </td>
                <td class="text-right align-middle">
                  <span
                    class="badge badge-custom bg-success-light text-success-dark"
                  >
                    {{ hitungPersentase(sudahBayarCustomer, nilaiProyek) }}
                  </span>
                </td>
              </tr>

              <tr>
                <td class="text-dark text-left align-middle sub-label-row">
                  b. Belum dibayar Pemberi Kerja
                </td>
                <td
                  class="text-right align-middle font-weight-medium text-danger"
                >
                  {{ formatRupiah(belumBayarCustomer) }}
                </td>
                <td class="text-right align-middle">
                  <span
                    class="badge badge-custom"
                    :class="
                      belumBayarCustomer > 0
                        ? 'bg-danger-light text-danger-dark'
                        : 'bg-secondary-light text-secondary-dark'
                    "
                  >
                    {{ hitungPersentase(belumBayarCustomer, nilaiProyek) }}
                  </span>
                </td>
              </tr>

              <tr class="border-top-dash row-header-group">
                <td
                  colspan="3"
                  class="font-weight-bold text-dark text-left align-middle"
                >
                  <div class="d-flex">
                    <span class="list-number">6.</span>
                    <span>Delivery Beban Project</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td class="text-dark text-left align-middle sub-label-row">
                  a. Sudah dibayar ke Mitra
                </td>
                <td
                  class="text-right align-middle font-weight-medium text-dark"
                >
                  {{ formatRupiah(sudahBayarMitra) }}
                </td>
                <td class="text-right align-middle">
                  <span
                    class="badge badge-custom bg-success-light text-success-dark"
                  >
                    {{ hitungPersentase(sudahBayarMitra, bebanProyek) }}
                  </span>
                </td>
              </tr>

              <tr>
                <td class="text-dark text-left align-middle sub-label-row">
                  b. Belum Dibayar Ke Mitra
                </td>
                <td
                  class="text-right align-middle font-weight-medium text-danger"
                >
                  {{ formatRupiah(belumBayarMitra) }}
                </td>
                <td class="text-right align-middle">
                  <span
                    class="badge badge-custom"
                    :class="
                      belumBayarMitra > 0
                        ? 'bg-danger-light text-danger-dark'
                        : 'bg-secondary-light text-secondary-dark'
                    "
                  >
                    {{ hitungPersentase(belumBayarMitra, bebanProyek) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="text-right mt-4 border-top pt-3">
      <b-button variant="secondary" @click="$emit('input', false)"
        >Tutup</b-button
      >
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'DetailProyekModal',
  props: {
    value: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    projectName: { type: String, default: '' },
    projectCode: { type: String, default: '' },
    nilaiProyek: { type: [String, Number], default: 0 },
    bebanProyek: { type: [String, Number], default: 0 },
    sudahDitagih: { type: [String, Number], default: 0 },
    belumDitagih: { type: [String, Number], default: 0 },
    sudahBayarCustomer: { type: [String, Number], default: 0 },
    belumBayarCustomer: { type: [String, Number], default: 0 },
    sudahBayarMitra: { type: [String, Number], default: 0 },
    belumBayarMitra: { type: [String, Number], default: 0 },
    formatRupiah: { type: Function, required: true },
  },
  methods: {
    hitungPersentase(pembilang, penyebut) {
      const num = parseFloat(pembilang) || 0
      const den = parseFloat(penyebut) || 0
      if (den === 0) return '0.00%'
      return ((num / den) * 100).toFixed(2) + '%'
    },
  },
}
</script>

<style scoped>
.custom-modal-info td {
  padding: 10px 0;
  font-size: 14px;
}

/* Group Header Style */
.row-header-group td {
  padding-top: 15px;
  padding-bottom: 5px;
}

/* Lebar penomoran judul utama (1., 2., 3., 4., 5.) */
.list-number {
  flex: 0 0 24px;
  display: inline-block;
}

/* Skenario Baru: Membuat teks sub-anak (a. b.) bergeser sejajar teks header di atasnya */
.sub-label-row {
  padding-left: 24px !important; /* Nilainya harus sama dengan width .list-number agar sejajar lurus */
}

.font-weight-medium {
  font-weight: 600;
}

.font-size-15 {
  font-size: 15px;
}

.border-top-dash {
  border-top: 1px dashed #dee2e6;
}

.badge-custom {
  display: inline-block;
  width: 70px;
  text-align: center;
  padding: 5px 0;
  font-size: 12px;
  font-weight: 700;
  border-radius: 4px;
}

.bg-success-light {
  background-color: #d1e7dd !important;
}
.text-success-dark {
  color: #0f5132 !important;
}

.bg-danger-light {
  background-color: #f8d7da !important;
}
.text-danger-dark {
  color: #842029 !important;
}

.bg-secondary-light {
  background-color: #e2e3e5 !important;
}
.text-secondary-dark {
  color: #41464b !important;
}
</style>
